import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import camelcaseKeys from 'camelcase-keys';
import { CSSTransition } from 'react-transition-group';

import { get } from 'helpers/api';
import moment from 'helpers/moment';
import _ from 'helpers/no_conflict_lodash';
import { clickAsLink } from 'helpers/link_helpers';
import { formatDate } from 'helpers/formatters/date_time_formats';

import TagsApi from 'bundles/tags/tags_api';
import taskApi from '../tasks/api';
import { sortTasks } from 'bundles/tasks/index/utils';

import RequirementsPanel from 'bundles/contact/requirements/requirements_panel';
import TagsCard from 'bundles/tags/tags_card';

import ActionFooter from './contact_preview/action_footer';
import ActionsDropdown from './contact_preview/actions_dropdown';
import ClickToCallApi from '../click_to_call/api';
import GroupFormModal from '../modals/group_form_modal';
import InitialsBubble from '../initials_bubble';
import PersonBody from './contact_preview/person_body';
import CompanyBody from './contact_preview/company_body';
import PreviewHeader from './contact_preview/preview_header';
import RecentActivities from './recent_activities';
import RelatedProperties from './contact_preview/related_properties';
import Tasks from './contact_preview/tasks';
import TasksCompleted from './contact_preview/tasks_completed';

import '../styles/contacts.scss';

const initialState = {
  followUpTask: {
    body: "",
    title: "",
    taskType: 1,
    dueDate: moment().add(1, 'd').format(),
    assigneeId: null,
    associatedModels: [],
    taskListItems: [],
  },
  editingTask: {
    body: "",
    title: "",
    taskType: 1,
    dueDate: moment().format(),
    assigneeId: null,
    associatedModels: [],
    taskListItems: [],
    completed: true,
    logged: true
  }
};

class ContactPreview extends React.Component {
  static propTypes = {
    callId: PropTypes.number,
    callListId: PropTypes.number,
    callListsAccess: PropTypes.bool.isRequired,
    contactId: PropTypes.number,
    contactType: PropTypes.string,
    crmEmail: PropTypes.string,
    currentUser: PropTypes.object,
    databaseMembers: PropTypes.array,
    displayActions: PropTypes.shape({
      endCall: PropTypes.string.isRequired,
      startCall: PropTypes.string.isRequired
    }),
    paths: PropTypes.shape({
      callLists: PropTypes.string.isRequired,
      contactPath: PropTypes.string.isRequired,
      groups: PropTypes.string.isRequired,
      show: PropTypes.string,
      tags: PropTypes.string.isRequired,
      tasks: PropTypes.string.isRequired,
      set: PropTypes.string.isRequired
    }),
    propertyTypeOptions: PropTypes.array,
    stateOptions: PropTypes.array,
    taskId: PropTypes.number,
    transactionOptions: PropTypes.array,
    onCompleteTask: PropTypes.func,
    onCycleTask: PropTypes.func.isRequired,
    onEndCall: PropTypes.func,
    onEndCallList: PropTypes.func.isRequired,
    onStartCall: PropTypes.func
  };

  intervals = {
    callPollTimer: null,
    durationTimer: null
  };

  state = {
    associateToCallList: false,
    call: null,
    callListCompleted: null,
    callList: null,
    callDuration: null,
    followUp: false,
    followUpTask: {
      ...initialState.followUpTask,
      assigneeId: this.props.currentUser.id
    },
    editingTask: {
      ...initialState.editingTask,
      assigneeId: this.props.currentUser.id
    },
    isCalling: !!this.props.callId,
    groups: [],
    newGroup: null,
    record: {},
    recordTasks: [],
    showAlert: false,
    showFollowUpAlert: false,
    showTasksErrorPane: false
  };

  taskErrors = []

  componentDidMount() {
    const { callId, displayActions: { startCall } } = this.props;

    this.getCallList();

    if (callId) {
      this.getCall(callId).then((call) => {
        if (call.active) {
          window[startCall]();
          this.setInterval('durationTimer', this.updateCallDuration, 1000);
          this.setInterval('callPollTimer', this.refreshCall, 1000);
        }
      });
    } else {
      this.getRecord();
    }

    const followUp = this.getSessionFlag("followUp");
    const associateToCallList = this.getSessionFlag("associateToCallList");
    const showAlert = this.getSessionFlag("showAlert");
    const showFollowUpAlert = this.getSessionFlag("showFollowUpAlert");

    this.setState({
      associateToCallList,
      followUp,
      showAlert,
      showFollowUpAlert
    });
  }

  componentDidUpdate(prevProps) {
    const {
      callListId,
      contactId,
      contactType,
      taskId
    } = this.props;

    if (prevProps.taskId !== taskId || prevProps.contactId !== contactId) {
      this.setSessionValue("editingTask", null);
      this.setSessionValue("followUpTask", null);
    }

    if (
      prevProps.callListId !== callListId ||
      (prevProps.callListId !== callListId && prevProps.taskId !== taskId)) {
      this.getCallList();
    } else if (
      prevProps.contactId !== contactId ||
      prevProps.contactType !== contactType ||
      prevProps.taskId !== taskId
    ) {
      this.getRecord();
    }
  }

  getSessionFlag = key => window.sessionStorage.getItem(key) === "true";
  setSessionValue = (key, value) => window.sessionStorage.setItem(key, value);

  getCall = id => (
    ClickToCallApi.getCall(id).then((call) => {
      if (call.active) {
        this.setState({ call });
      } else {
        this.handleCallEnded();
      }

      return call;
    })
  );

  getRecord = () => {
    const {
      callListId,
      contactId,
      contactType,
      currentUser,
      paths,
      taskId
    } = this.props;

    const { callList } = this.state;

    if (contactId == null) return;

    get(paths.contactPath, {
      params: {
        preview_overlay: {
          call_list_id: callListId,
          record_id: contactId,
          record_type: contactType,
          task_id: taskId
        }
      }
    })
      .then((response) => {
        const record = response ? (response.person || response.company) : {};
        if (callListId == null && record.id == null) return;

        let nextState = {
          canAccessCrm: record.canAccessCrm,
          record: {
            ...camelcaseKeys(record)
          },
          showAlert: false,
          showFollowUpAlert: false
        };

        if (record.canAccessCrm) {
          this.getTasks(record.id, record.contactType);

          const association = {
            associatedModelId: record.id,
            associatedModelType: record.contactType,
            name: record.name
          };

          let editingTask = {
            ...initialState.editingTask,
            assigneeId: currentUser.id,
            associatedModels: [association]
          };

          if (callListId && callList) {
            if (taskId && callList.tasks) {
              editingTask = callList.tasks.find(task => task.id === taskId) || initialState.editingTask;
            }

            editingTask = {
              ...editingTask,
              completed: true
            };
          }

          const sessionTask = JSON.parse(window.sessionStorage.getItem("editingTask"));
          if (sessionTask != null) {
            editingTask = sessionTask;
          }

          let followUpTask = {
            ...initialState.followUpTask,
            title: `Follow up with ${record.name}`,
            assigneeId: currentUser.id,
            associatedModels: editingTask.associatedModels.map(({ id, ...association }) => association)
          };

          const sessionFollowUpTask = JSON.parse(window.sessionStorage.getItem("followUpTask"));
          if (sessionFollowUpTask != null) {
            followUpTask = sessionFollowUpTask;
          }

          nextState = {
            ...nextState,
            editingTask: {
              ...editingTask,
              dueDate: moment.isMoment(editingTask.dueDate) ? editingTask.dueDate : moment(editingTask.dueDate)
            },
            followUpTask
          };
        }

        this.setState(nextState, () => {
          this.getGroups();
        });
      })
      .catch((error) => {
        console.error(error);
      });
  };

  getCallList = () => {
    const {
      callListId,
      paths,
      taskId
    } = this.props;

    if (callListId == null || taskId == null) return;

    axios.get(paths.callLists, {
      params: {
        preview_overlay: {
          call_list_id: callListId,
          task_id: taskId
        }
      }
    })
      .then((response) => {
        const callListData = response.data;
        const callList = camelcaseKeys(callListData, { deep: true });
        let sortedTasks, currentTaskId, editingTask;
        if (callList.tasks && callList.tasks.length > 0) {
          sortedTasks = sortTasks(callList.tasks);
          currentTaskId = taskId || sortedTasks[0].id;
          if (currentTaskId) {
            editingTask = sortedTasks.find(task => task.id === currentTaskId);
          }
        }

        const sessionTask = JSON.parse(window.sessionStorage.getItem("editingTask"));
        if (sessionTask != null) {
          editingTask = sessionTask;
        }

        let dueDate;
        if (editingTask) {
          dueDate = moment.isMoment(editingTask.dueDate) ? editingTask.dueDate : moment(editingTask.dueDate);
        }

        callList.tasks = sortedTasks || [];

        this.setState({
          callListCompleted: null,
          callList: callList,
          canAccessCrm: callList.canAccessCrm,
          editingTask: {
            ...editingTask,
            dueDate: dueDate,
            completed: true
          }
        });
        this.getRecord();
      })
      .catch(() => {
        this.getRecord();
      });
  };

  getGroups = () => {
    const { record } = this.state;
    if (!record.canAccessCrm) return;

    const databaseId = record.ownerType === 'Database' ? record.ownerId : null;

    get(record.paths.groups, { params: { databaseId } }).then(({ groups }) => {
      this.setState({ groups });
    });
  };

  getTasks = (assigneeId, assigneeType) => {
    if (!assigneeId) {
      this.setState({ recordTasks: [] });
      return null;
    }

    const { paths } = this.props;
    axios.get(paths.tasks, {
      params: {
        "q[completed]": true,
        "q[task_associations_associated_model_id_eq]": assigneeId,
        "q[task_associations_associated_model_type_eq]": assigneeType
      }
    }).then((response) => {
      const recordTasks = _.map(response.data.tasks, task => this.taskFromApi(task));
      this.setState({ recordTasks });
    });
  };

  refreshCall = () => {
    const { call } = this.state;

    if (call && call.id) {
      this.getCall(call.id);
    }
  };

  taskFromApi = (apiTask) => {
    return {
      body: apiTask.body,
      calendarImport: apiTask.calendar_import,
      completedAt: formatDate(apiTask.completed_at),
      hasCrmEmail: apiTask.has_crm_email,
      id: apiTask.id,
      type: apiTask.task_type,
      title: apiTask.title
    };
  };

  transformTaskData = (taskData) => {
    return {
      ...taskData,
      dueDate: taskData.dueDate.toString()
    };
  };

  validateTask = (task) => {
    const validationErrors = [];
    if (task.title === '') {
      validationErrors.push('Summary is required.');
    }
    if (task.dueDate === null) {
      validationErrors.push('Due date is required.');
    }
    if (task.assigneeId === null) {
      validationErrors.push('Assignee is required.');
    }
    return validationErrors.length ? validationErrors.join(' ') : null;
  }

  isValidTask = task => (!this.validateTask(task));

  handleFollowUp = () => {
    this.setState(prevState =>(
      { followUp: !prevState.followUp }
    ), () => {
      this.setSessionValue("followUp", this.state.followUp);
    });
  };

  handleStartCall = () => {
    const { displayActions: { startCall }, onStartCall } = this.props;
    const { record } = this.state;

    const name = record.name || record.phone;
    ClickToCallApi.startPreviewCall(name, record.phone).then(
      (response) => {
        const windowStart = window[startCall] || (() => {});
        windowStart();
        if (typeof onStartCall === 'function') onStartCall();

        this.setState({
          call: response.call,
          callDuration: moment.duration(0),
          isCalling: true
        });

        this.setInterval('durationTimer', this.updateCallDuration, 1000);
        this.setInterval('callPollTimer', this.refreshCall, 5000);

        alert(response.message);
      },
      () => alert('There was a problem calling you - please try again later.')
    );
  };

  handleEndCall = () => {
    const { call } = this.state;

    if (call) {
      ClickToCallApi.endCall(call.id);
    }
  };

  handleCallEnded = () => {
    const { displayActions: { endCall }, onEndCall } = this.props;

    this.clearInterval('callPollTimer');
    this.clearInterval('durationTimer');

    window[endCall]();
    if (typeof onEndCall === 'function') onEndCall();

    this.setState({
      callDuration: null,
      call: null,
      isCalling: false
    });
  };

  clearInterval = (name) => {
    if (this.intervals[name]) {
      window.clearInterval(this.intervals[name]);
      this.intervals[name] = null;
    }
  };

  setInterval = (name, fn, interval) => {
    this.intervals[name] = window.setInterval(fn, interval);
  };

  validNextTask = (taskId) => {
    const tasks = _.get(this.state, 'callList.tasks') || [];

    const currentTaskIndex = tasks.findIndex(task => task.id === taskId) || -1;
    const validTaskLength = tasks.filter(task => !task.completedAt).length;
    return !((currentTaskIndex + 1) >= validTaskLength);
  };

  handleNextRecord = () => {
    const { taskId, onCycleTask } = this.props;
    const { callList: { tasks, incompleteCalls } } = this.state;

    // Check for end of list or if is complete

    let currentTaskIndex = tasks.findIndex(task => task.id === taskId);
    const validTaskLength = tasks.filter(task => !task.completedAt).length;

    let nextTask = currentTaskIndex < validTaskLength - 1 ? tasks[currentTaskIndex + 1] : {};

    if (validTaskLength === 0) this.handleCallListCompleted();

    if (((currentTaskIndex + 1) >= validTaskLength) || nextTask.completedAt) {
      if (incompleteCalls > 0) {
        currentTaskIndex = 0;
        while (currentTaskIndex < validTaskLength) {
          if (tasks[currentTaskIndex].completedAt === null) {
            currentTaskIndex--;
            break;
          }
          currentTaskIndex++;
        }
      }
    } else if (currentTaskIndex >= validTaskLength) {
      this.handleCallListCompleted();
    }

    nextTask = tasks[currentTaskIndex + 1];
    const nextContact = nextTask.associatedModels.find((model) => {
      return (model.associatedModelType === "Contacts::Person") || (model.associatedModelType === "Contacts::Company");
    }) || {};

    this.setState({
      showAlert: false,
      showFollowUpAlert: false
    }, () => {
      this.setSessionValue("showAlert", false);
      this.setSessionValue("showFollowUpAlert", false);
    });

    onCycleTask(nextContact, nextTask);
  };

  updateCallDuration = () => {
    this.setState(prevState => ({
      callDuration: prevState.call.createdAt ? moment.duration(moment().diff(prevState.call.createdAt)) : null
    }));
  };

  validPrevTask = (taskId) => {
    const tasks = _.get(this.state, 'callList.tasks') || [];

    const currentTaskIndex = tasks.findIndex(task => task.id === taskId) || -1;

    let prevTaskIndex = currentTaskIndex - 1;
    while (prevTaskIndex > -1) {
      const task = tasks[prevTaskIndex];
      if (task.completedAt == undefined) break;

      prevTaskIndex = prevTaskIndex - 1;
    }

    if (prevTaskIndex === -1) return null;
    return tasks[prevTaskIndex];
  };

  handlePrevRecord = () => {
    const { taskId, onCycleTask } = this.props;

    const prevTask = this.validPrevTask(taskId);
    if (prevTask === null) return null;

    const prevContact = prevTask.associatedModels.find((model) => {
      return (model.associatedModelType === "Contacts::Person") || (model.associatedModelType === "Contacts::Company");
    }) || {};

    this.setState({
      showAlert: false,
      showFollowUpAlert: false,
    }, () => {
      this.setSessionValue("showAlert", false);
      this.setSessionValue("showFollowUpAlert", false);
    });

    onCycleTask(prevContact, prevTask);
  };

  handleCallListCompleted = () => {
    this.setState(prevState => ({
      callListCompleted: prevState.callList.name,
      editingTask: initialState.editingTask,
      followUpTask: initialState.followUpTask
    }));
  };

  handleEndCallList = () => {
    const { onEndCallList } = this.props;

    this.setState({
      callList: {},
      callListCompleted: null,
    });

    onEndCallList();
    this.handleEndCall();
  };

  handleAddTag = (tag) => {
    const { record } = this.state;
    const tagsPath = {
      tags: record.paths.tags
    };
    const api = new TagsApi(tagsPath);
    api.saveTag(tag).then((newTags) => {
      this.setState(prevState => ({
        record: {
          ...prevState.record,
          tags: newTags
        }
      }));
    });
  };

  handleRemoveTag = (tag) => {
    const { record } = this.state;
    const tagsPath = {
      tags: record.paths.tags
    };
    const api = new TagsApi(tagsPath);
    api.deleteTag(tag).then((newTags) => {
      this.setState(prevState => ({
        record: {
          ...prevState.record,
          tags: newTags
        }
      }));
    });
  };

  handleUpdateTask = () => {
    const { editingTask } = this.state;

    if (editingTask.id == null) {
      return;
    }
    const taskData = this.transformTaskData(editingTask);
    taskApi.updateTask(taskData);
  };

  debouncedUpdateTask = _.debounce(this.handleUpdateTask, 1000);

  handleSaveTasks = async () => {

    // Check for end of list or if is complete

    const { onCompleteTask } = this.props;
    const { followUpTask, record, editingTask, followUp, callList } = this.state;
    const tasks = callList ? callList.tasks : [];
    const taskData = this.transformTaskData(editingTask);

    const taskSave = editingTask.id ? taskApi.updateTask(taskData) : taskApi.addTask(taskData);
    const currentTaskIndex = tasks.findIndex(task => task.id === editingTask.id);

    if (this.isValidTask(followUpTask) && followUp) {
      const followUpTaskData = this.transformTaskData(followUpTask);
      await Promise.all([taskSave, taskApi.addTask(followUpTaskData)]).then(() => {
        this.setState({
          associateToCallList: false,
          followUp: false,
          showFollowUpAlert: true,
          showAlert: true,
          showTasksErrorPane: false
        }, () => {
          this.setSessionValue("showFollowUpAlert", true);
          this.setSessionValue("showAlert", true);
        });
      }).catch((errors) => {
        this.taskErrors = errors;
        this.setState({ showTasksErrorPane: true });
      });
    } else {
      taskSave.then(() => {
        this.setState(prevState => ({
          associateToCallList: false,
          callList: {
            ...prevState.callList,
            incompleteCalls: prevState.callList ? prevState.callList.incompleteCalls - 1 : 0
          },
          followUp: false,
          showFollowUpAlert: false,
          showAlert: true,
          showTasksErrorPane: false
        }), () => {
          this.setSessionValue("showFollowUpAlert", false);
          this.setSessionValue("showAlert", true);
        });
      }).catch((errors) => {
        this.taskErrors = errors;
        this.setState({ showTasksErrorPane: true });
      });
    }

    if (currentTaskIndex >= 0) {
      tasks[currentTaskIndex].completedAt = true;

      if (onCompleteTask) onCompleteTask(tasks[currentTaskIndex]);

      this.setState(prevState => ({
        callList: {
          ...prevState.callList,
          tasks: tasks
        }
      }));
    }
    this.setSessionValue("associateToCallList", false);
    this.setSessionValue("followUp", false);

    this.getTasks(record.id, record.contactType);
  };

  handleTasksErrorPaneDismiss = () => {
    this.setState({ showTasksErrorPane: false });
  }

  renderPreviewHeader() {
    const { callListId, taskId } = this.props;
    const { callDuration, callList, isCalling, record } = this.state;

    return (
      <PreviewHeader
        callDuration={callDuration}
        callList={callList}
        callListId={callListId}
        isCalling={isCalling}
        record={record}
        validNextTask={this.validNextTask(taskId)}
        validPrevTask={this.validPrevTask(taskId)}
        onNextRecord={this.handleNextRecord}
        onPrevRecord={this.handlePrevRecord}
      />
    );
  }

  handleAddToGroup = (group) => {
    const { record } = this.state;
    axios.patch(group.url, { member_ids: [record.id] }).then(() => {
      this.getGroups();
    });
  };

  handleDeleteFromGroup = (group) => {
    const { record } = this.state;
    axios.patch(group.deleteMembersUrl, { member_ids: [record.id] }).then(() => {
      this.getGroups();
    });
  };

  handleGroupSave = (group) => {
    const { record } = this.state;

    axios.post(record.paths.groups, { group: group, member_ids: [record.id] }).then(() => {
      this.setState({ newGroup: null });
      this.getGroups();
    });
  };

  handleCreateGroup = () => this.setState({ newGroup: { id: null, name: "" } });
  handleCancelCreateGroup = () => this.setState({ newGroup: null });

  handleAddAssociation = (associations, modelType) => {
    const associatedModels = this.state.editingTask.associatedModels;

    associations.forEach((association) => {
      if (association.associatedModelId === undefined) {
        associatedModels.push(
          {
            associatedModelId: association.id,
            name: association.name || association.displayName,
            associatedModelType: modelType
          }
        );
      }
    });

    this.setState(prevState => ({
      editingTask: {
        ...prevState.editingTask,
        associatedModels: associatedModels
      },
      followUpTask: {
        ...prevState.followUpTask,
        associatedModels: associatedModels.map(({ id, ...association }) => association)
      }
    }), () => {
      this.setSessionValue("editingTask", JSON.stringify(this.state.editingTask));
      this.setSessionValue("followUpTask", JSON.stringify(this.state.followUpTask));
    });
  };

  handleRemoveAssociation = (id, modelType) => {
    const associatedModels = this.state.editingTask.associatedModels.filter((association) => {
      return !(association.associatedModelId === id && association.associatedModelType === modelType);
    });

    this.setState(prevState => ({
      editingTask: {
        ...prevState.editingTask,
        associatedModels: associatedModels
      },
      followUpTask: {
        ...prevState.followUpTask,
        associatedModels: associatedModels.map(({ id, ...association }) => association)
      }
    }), () => {
      this.setSessionValue("editingTask", JSON.stringify(this.state.editingTask));
      this.setSessionValue("followUpTask", JSON.stringify(this.state.followUpTask));
    });
  };

  handleTaskChange = (value, key) => {
    this.setState(prevState => ({
      editingTask: {
        ...prevState.editingTask,
        [key]: value
      }
    }), () => {
      this.debouncedUpdateTask();
      this.setSessionValue("editingTask", JSON.stringify(this.state.editingTask));
      if (key === "body") {
        this.setState(prevState => ({
          followUpTask: {
            ...prevState.followUpTask,
            body: value
          }
        }), () => {
          this.setSessionValue("followUpTask", JSON.stringify(this.state.followUpTask));
        });
      }
    });
  };

  handleFollowUpTaskChange = (value, key) => {
    this.setState(prevState => ({
      followUpTask: {
        ...prevState.followUpTask,
        [key]: value
      }
    }), () => {
      this.setSessionValue("followUpTask", JSON.stringify(this.state.followUpTask));
    });
  };

  handleRemoveTaskListAssociation = (id) => {
    this.setState(prevState => ({
      editingTask: {
        ...prevState.editingTask,
        taskListItems: prevState.editingTask.taskListItems.filter(obj => obj.id !== id)
      }
    }), () => {
      this.setSessionValue("editingTask", JSON.stringify(this.state.editingTask));
    });
  };

  handleToggleCallList = () => {
    const { associateToCallList, editingTask } = this.state;

    if (associateToCallList) {
      this.setState(prevState => ({
        followUpTask: {
          ...prevState.followUpTask,
          taskListItems: []
        },
        associateToCallList: !prevState.associateToCallList
      }), () => {
        this.setSessionValue("followUpTask", JSON.stringify(this.state.followUpTask));
        this.setSessionValue("associateToCallList", this.state.associateToCallList);
      });
    } else {
      const items = editingTask.taskListItems.map(({ id, taskId, ...listItem }) => listItem);
      this.setState(prevState => ({
        followUpTask: {
          ...prevState.followUpTask,
          taskListItems: items
        },
        associateToCallList: !prevState.associateToCallList
      }), () => {
        this.setSessionValue("followUpTask", JSON.stringify(this.state.followUpTask));
        this.setSessionValue("associateToCallList", this.state.associateToCallList);
      });
    }
  };

  recordPage = (e) => {
    const { record } = this.state;
    clickAsLink(window, record.paths.show, e);
  };

  render() {
    const { callListCompleted, canAccessCrm } = this.state;

    if (callListCompleted) {
      return (
        <div className="preview-overlay-content">
          {this.renderCallListComplete()}
          {this.renderActionFooter()}
        </div>
      );
    } else {
      return (
        <div className="preview-overlay-content">
          {this.renderPreviewHeader()}
          {this.renderActionHeader()}
          <div className="preview-overlay-scroll-drawer">
            {this.renderPreviewBody()}
            <hr/>
            {this.renderTags()}
            {this.renderRequirements()}
            {this.renderRelatedProperties()}
            {canAccessCrm && (
              <React.Fragment>
                {this.renderRecentActivities()}
                <hr/>
                {this.renderTasksCompleted()}
                {this.renderActivities()}
              </React.Fragment>
            )}
          </div>
          {canAccessCrm && this.renderActionFooter()}
          {this.renderGroupModal()}
        </div>
      );
    }
  }

  renderRequirements = () => {
    const { record } = this.state;
    const { contactType } = this.props;

    if (contactType == 'Contacts::Company' || record.requirementsSets == null || record.id == null) {
      return;
    }

    return (
      <React.Fragment>
        <hr/>
        <div className="preview-overlay-requirements">
          <RequirementsPanel
            newPath={`/contacts/people/${record.id}/requirements_sets/new`}
            requirements={record.requirementsSets}
          />
        </div>
      </React.Fragment>
    );
  };

  renderRelatedProperties = () => {
    const { record: { relatedProperties } } = this.state;
    if (!relatedProperties || relatedProperties.length < 1) return null;

    return (
      <React.Fragment>
        <hr/>
        <div className="preview-overlay-properties">
          <RelatedProperties properties={relatedProperties} />
        </div>
      </React.Fragment>
    );
  }

  renderRecentActivities = () => {
    const { recordTasks } = this.state;
    if (recordTasks.length > 0) {
      return (
        <React.Fragment>
          <hr/>
          <div className="preview-overlay-recent-activities">
            <RecentActivities activities={recordTasks} />
          </div>
        </React.Fragment>
      );
    }
  };

  renderActionHeader = () => {
    const { canAccessCrm, record, groups } = this.state;

    return (
      <div className="preview-overlay-action-header row">
        <div className="col-md-3 pl-0 pr-0">
          <InitialsBubble
            preview={true}
            text={record.initials}
          />
        </div>
        <div className="top-buffer-sm">
          <button className="btn btn-cancel mr-3 view-record-btn" onClick={this.recordPage}>
            View Contact Details
          </button>
          {canAccessCrm && (
            <ActionsDropdown
              groups={groups}
              selectedId={record.id}
              onAddToGroup={this.handleAddToGroup}
              onCreateGroup={this.handleCreateGroup}
              onDeleteFromGroup={this.handleDeleteFromGroup}
            />
          )}
        </div>
        <div className="col-md-8" />
      </div>
    );
  };

  renderPreviewBody = () => {
    const { crmEmail, currentUser } = this.props;
    const { record } = this.state;

    if (record.contactType === 'Contacts::Company') {
      return (
        <CompanyBody record={record} user={currentUser} />
      );
    } else {
      return (
        <PersonBody crmEmail={crmEmail} record={record} user={currentUser} />
      );
    }
  };

  renderTags = () => {
    const { record } = this.state;
    if (Object.keys(record).length === 0) return;
    const tagsPath = {
      tags: record.paths.tags
    };
    return (
      <div className="preview-overlay-tags">
        <TagsCard
          editable={record.canManageTags}
          paths={tagsPath}
          tags={record.tags}
          onAdd={this.handleAddTag}
          onRemove={this.handleRemoveTag}
        />
      </div>
    );
  };

  renderActivities = () => {
    const { callListId, callListsAccess, currentUser, databaseMembers } = this.props;
    const {
      associateToCallList,
      editingTask,
      followUp,
      followUpTask,
      record,
      showAlert,
      showTasksErrorPane
    } = this.state;

    if (showAlert) return null;

    return (
      <Tasks
        associateToCallList={associateToCallList}
        callListId={callListId}
        callListsAccess={callListsAccess}
        currentUser={currentUser}
        databaseMembers={databaseMembers}
        editingTask={editingTask}
        followUp={followUp}
        followUpTask={followUpTask}
        record={record}
        showErrorPane={showTasksErrorPane}
        taskErrors={this.taskErrors}
        onAddAssociation={this.handleAddAssociation}
        onErrorPaneDismiss={this.handleTasksErrorPaneDismiss}
        onFollowUp={this.handleFollowUp}
        onFollowUpTaskChange={this.handleFollowUpTaskChange}
        onRemoveAssociation={this.handleRemoveAssociation}
        onRemoveTaskListAssociation={this.handleRemoveTaskListAssociation}
        onTaskChange={this.handleTaskChange}
        onToggleCallList={this.handleToggleCallList}
      />
    );
  };

  renderActionFooter = () => {
    const { callListId, currentUser } = this.props;
    const { editingTask, isCalling, showAlert, callListCompleted, record } = this.state;

    return (
      <ActionFooter
        callListCompleted={callListCompleted !== null}
        callListId={callListId}
        currentUser={currentUser}
        editingTask={editingTask}
        isCalling={isCalling}
        isValidTask={this.validateTask}
        phoneNormalized={record.phone}
        taskComplete={showAlert}
        onEndCall={this.handleEndCall}
        onEndCallList={this.handleEndCallList}
        onNextRecord={this.handleNextRecord}
        onSaveTasks={this.handleSaveTasks}
        onStartCall={this.handleStartCall}
      />
    );
  };

  renderTasksCompleted() {
    const {
      editingTask,
      followUpTask,
      showAlert,
      showFollowUpAlert
    } = this.state;

    return (
      <CSSTransition
        classNames="task-completed"
        in={showAlert}
        timeout={2000}
        unmountOnExit
      >
        <div className="preview-overlay-activity">
          <TasksCompleted
            editingTask={editingTask}
            followUpTask={followUpTask}
            showFollowUp={showFollowUpAlert && this.isValidTask(followUpTask)}
          />
        </div>
      </CSSTransition>
    );
  }

  renderCallListComplete() {
    const { callListCompleted } = this.state;
    return (
      <React.Fragment>
        <div className="preview-overlay-content-header">
          <b className="title record-name">Call List Complete</b>
          <small className="subtitle record-call-list">
            {callListCompleted} - 0 Calls Remaining
          </small>
        </div>
        <div className="preview-overlay-action-header top-buffer-sm">
          <span> You have completed all calls in: </span>
          <h3 className="call-list-title top-buffer-sm">{callListCompleted}</h3>
        </div>
      </React.Fragment>
    );
  }

  renderGroupModal() {
    const { newGroup } = this.state;
    return (
      <GroupFormModal
        group={newGroup}
        shown={newGroup != null}
        onCancel={this.handleCancelCreateGroup}
        onSubmit={this.handleGroupSave}
      />
    );
  }
}

export default ContactPreview;
